// VisorCamarasFrio.js
import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import dayjs from "dayjs";
import axios from "axios";
import {
  Snowflake,
  RefreshCw,
  Package,
  Thermometer,
  Pause,
  Play,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Truck,
  Clock,
  X,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Card, CardHeader, CardTitle, CardExtra, CardContent } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from "./components/ui/dialog";
import { toast } from "./lib/toast";

/* =================== Config =================== */
// Los endpoints de cámaras viven en ws.packingapp.cl (mismos que usa la app RN),
// no en api.packingapp.cl. Si más adelante los migras a ServiceFamilyGrowers.asmx
// con Bearer, solo hay que cambiar estas constantes.
const WS_BASE = "https://ws.packingapp.cl/webservice.asmx";
const EP_CAMARAS = `${WS_BASE}/GetCamaras`;
const EP_PALLETS_EN_CAMARA = `${WS_BASE}/GetPalletsEnCamara`;
const EP_FOLIO_TARJA = `${WS_BASE}/GetFolioTarja`;
const EP_HISTORIAL_TEMPERATURA = `${WS_BASE}/GetHistorialTemperaturaPallet`;
const EP_HISTORIAL_MOVIMIENTOS = `${WS_BASE}/GetHistorialMovimientosPallet`;
const EP_TIEMPO_PROMEDIO_DESPACHO = `${WS_BASE}/GetTiempoPromedioDespacho`;
const EP_FOLIOS_DESPACHADOS = `${WS_BASE}/GetFoliosDespachados`;

const DATA_REFRESH_MS = 30_000; // cada cuánto se vuelve a pedir data al backend
const CLOCK_TICK_MS = 15_000;   // cada cuánto se recalcula el tiempo transcurrido en pantalla

const ACCENT = "#00897B"; // acento cold storage, mismo del design system de la app

// Cámaras donde un pallet con folio ya despachado (documento de Salida, tipoFolio='S')
// debe dejar de mostrarse, aunque el sistema de inventario todavía no registre el
// movimiento de salida de la cámara. Comparación por nombre ya "trimeado" (char() con relleno).
const NOMBRES_CAMARAS_TERMINO = ["Cámara Prod Terminado 9", "Cámara Prod Terminado 10"];

/* =================== Helpers =================== */

// Estos endpoints (Sub + Response.Write) devuelven el array/JSON directo,
// sin el envoltorio {"d": "..."} que sí usan los ScriptMethod. Este parser
// cubre igual ambos casos por si el backend cambia de patrón más adelante.
const parseResponse = (data) => {
  try {
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
      if ("d" in data) return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      return data;
    }
    return JSON.parse(String(data ?? "[]"));
  } catch {
    return null;
  }
};

// Las columnas char() de SQL Server vienen con espacios de relleno; hay que
// limpiarlas antes de mostrarlas (ver GetFolioTarja: folio, especie, embaleja, etc.)
const trim = (v) => (typeof v === "string" ? v.trim() : v);

// Convierte una cantidad de horas (float) en un texto legible "Xd Yh" / "Xh Ym" / "Ym"
const formatHorasDuracion = (horas) => {
  if (horas == null || Number.isNaN(Number(horas))) return "—";
  const totalMin = Math.round(Number(horas) * 60);
  const days = Math.floor(totalMin / 1440);
  const hrs = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

const horasTranscurridas = (fechaHora) => dayjs().diff(dayjs(fechaHora), "minute") / 60;

// "alerta": excedió horasAlertaMax | "normal": dentro del tiempo | "neutral": la cámara no tiene límite definido
const getPalletEstado = (pallet) => {
  if (pallet.horasAlertaMax == null) return "neutral";
  return horasTranscurridas(pallet.fechaHora) > Number(pallet.horasAlertaMax) ? "alerta" : "normal";
};

// Se mantiene como wrapper para no tocar el resto de los usos existentes (conteo de alertas, orden de cámaras, etc.)
const isPalletEnAlerta = (pallet) => getPalletEstado(pallet) === "alerta";

/* =================== Subcomponentes =================== */

const ESTADO_ESTILOS = {
  alerta: "bg-red-50 border border-red-200 hover:bg-red-100",
  normal: "bg-green-50 border border-green-200 hover:bg-green-100",
  neutral: "bg-gray-50 border border-gray-200 hover:bg-gray-100",
};

const PalletChip = ({ pallet, onClick }) => {
  const estado = getPalletEstado(pallet);
  const temp = pallet.ultimaTemperatura;
  const tempLabel = temp != null && temp !== "" ? `${Number(temp).toFixed(1)}°C` : "—";

  return (
    <button
      type="button"
      onClick={() => onClick(pallet)}
      className={`flex flex-col gap-1 rounded-lg px-3 py-2 text-left transition-colors ${ESTADO_ESTILOS[estado]}`}
    >
      {/* Folio: dato prioritario, en su propia línea para que nunca se trunque */}
      <span className="flex items-center gap-1">
        <Package className={`h-3.5 w-3.5 shrink-0 ${estado === "alerta" ? "text-red-500" : "text-gray-400"}`} />
        <span className="font-bold text-gray-900 text-sm leading-none tabular-nums">
          {pallet.folioPallet}
        </span>
      </span>
      {/* Temperatura: dato secundario, debajo del folio */}
      <span className="flex items-center gap-1 text-[11px] pl-[18px] text-gray-500">
        <Thermometer className="h-3 w-3" />
        <span className="tabular-nums">{tempLabel}</span>
      </span>
    </button>
  );
};

const Leyenda = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-4">
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
      Dentro del tiempo máximo
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
      Excedió el tiempo máximo
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      Sin límite de tiempo definido
    </span>
  </div>
);

const MetricCard = ({ icon: Icon, label, value, sublabel }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
      style={{ background: `${ACCENT}1A`, color: ACCENT }}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-gray-400 truncate">{label}</div>
      <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
      {sublabel && <div className="text-[11px] text-gray-400 truncate">{sublabel}</div>}
    </div>
  </div>
);

const CamaraCard = ({ camara, pallets, onSelectPallet }) => {
  const ordenados = useMemo(
    () => [...pallets].sort((a, b) => dayjs(a.fechaHora).valueOf() - dayjs(b.fechaHora).valueOf()),
    [pallets]
  );
  const alertasCount = ordenados.filter(isPalletEnAlerta).length;

  const promedioHorasCamara = useMemo(() => {
    if (ordenados.length === 0) return null;
    const total = ordenados.reduce((acc, p) => acc + horasTranscurridas(p.fechaHora), 0);
    return total / ordenados.length;
  }, [ordenados]);

  // Temperatura promedio de los pallets de esta cámara (solo los que tienen lectura válida)
  const promedioTemperaturaCamara = useMemo(() => {
    const valores = ordenados
      .filter((p) => p.ultimaTemperatura != null && p.ultimaTemperatura !== "")
      .map((p) => Number(p.ultimaTemperatura))
      .filter((n) => !Number.isNaN(n));
    if (valores.length === 0) return null;
    return valores.reduce((a, b) => a + b, 0) / valores.length;
  }, [ordenados]);

  return (
    <Card className={alertasCount > 0 ? "ring-2 ring-red-300" : ""}>
      <CardHeader>
        <CardTitle className="flex flex-col items-start gap-0.5">
          <span className="flex items-center gap-2">
            <Snowflake className="h-4 w-4" style={{ color: ACCENT }} />
            <span className="truncate">{camara.nombre}</span>
          </span>
          {/* Debajo del título: temperatura promedio de los pallets de la cámara */}
          <span className="flex items-center gap-1 text-xs font-normal text-gray-400">
            <Thermometer className="h-3 w-3" />
            {promedioTemperaturaCamara != null
              ? `${promedioTemperaturaCamara.toFixed(1)}°C promedio`
              : "Sin lecturas de temperatura"}
          </span>
        </CardTitle>
        <CardExtra>
          {alertasCount > 0 && (
            <Badge variant="red">
              {alertasCount} {alertasCount === 1 ? "alerta" : "alertas"}
            </Badge>
          )}
        </CardExtra>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge variant="gray">
            <span className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              {ordenados.length} {ordenados.length === 1 ? "pallet" : "pallets"}
            </span>
          </Badge>
          {promedioHorasCamara != null && (
            <Badge variant="gray" title="Tiempo promedio en cámara">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatHorasDuracion(promedioHorasCamara)} prom.
              </span>
            </Badge>
          )}
        </div>
        {ordenados.length === 0 ? (
          <div className="text-gray-400 text-sm py-2">Sin pallets en esta cámara.</div>
        ) : (
          <div
            className="grid gap-2 max-h-[28rem] overflow-y-auto pr-1"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))" }}
          >
            {ordenados.map((p) => (
              <PalletChip
                key={`${p.folioPallet}-${p.idCamara}-${p.fechaHora}`}
                pallet={p}
                onClick={onSelectPallet}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className="text-xs text-gray-400">{label}</div>
    <div className="font-medium text-gray-800 truncate">{value || "—"}</div>
  </div>
);

/* =================== Componente principal =================== */

const VisorCamarasFrio = () => {
  const [camaras, setCamaras] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [, setTick] = useState(0); // fuerza recalculo de "tiempo transcurrido" entre fetchs

  // Folios con documento de Salida (tipoFolio='S'): se usan para ocultar pallets
  // ya despachados en las cámaras de término, aunque sigan apareciendo en GetPalletsEnCamara.
  const [foliosDespachados, setFoliosDespachados] = useState(() => new Set());

  // Modal de detalle de tarja
  const [tarjaModalOpen, setTarjaModalOpen] = useState(false);
  const [tarjaFolio, setTarjaFolio] = useState(null);
  const [tarjaLoading, setTarjaLoading] = useState(false);
  const [tarjaLineas, setTarjaLineas] = useState([]);
  const tarjaCacheRef = useRef(new Map());

  // Pestaña activa del modal de detalle
  const [activeTab, setActiveTab] = useState("tarja");

  // Historial de temperaturas
  const [historialTemp, setHistorialTemp] = useState([]);
  const [historialTempLoading, setHistorialTempLoading] = useState(false);
  const historialTempCacheRef = useRef(new Map());

  // Foto ampliada (lightbox) de una lectura de temperatura, subida desde la app
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  // Historial de movimientos entre cámaras
  const [historialMov, setHistorialMov] = useState([]);
  const [historialMovLoading, setHistorialMovLoading] = useState(false);
  const historialMovCacheRef = useRef(new Map());

  const isFetchingRef = useRef(false);

  // Métrica histórica: tiempo promedio hasta despacho
  const [tiempoPromedioDespacho, setTiempoPromedioDespacho] = useState(null);
  const [palletsConsiderados, setPalletsConsiderados] = useState(null);
  const [metricasLoading, setMetricasLoading] = useState(false);

  const fetchMetricasDespacho = useCallback(async () => {
    setMetricasLoading(true);
    try {
      const res = await axios.get(EP_TIEMPO_PROMEDIO_DESPACHO);
      const data = parseResponse(res.data);
      const row = Array.isArray(data) ? data[0] : data;
      setTiempoPromedioDespacho(
        row?.horasPromedioDespacho != null ? Number(row.horasPromedioDespacho) : null
      );
      setPalletsConsiderados(
        row?.palletsConsiderados != null ? Number(row.palletsConsiderados) : null
      );
    } catch (e) {
      console.error(e);
    } finally {
      setMetricasLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetricasDespacho();
  }, [fetchMetricasDespacho]);

  // idCamara de las cámaras de término (por nombre, trimeado por el relleno de char())
  const idsCamarasTermino = useMemo(() => {
    const set = new Set();
    camaras.forEach((c) => {
      if (NOMBRES_CAMARAS_TERMINO.includes(trim(c.nombre))) set.add(c.idCamara);
    });
    return set;
  }, [camaras]);

  // Pallets a mostrar: se excluyen los que están en una cámara de término
  // Y cuyo folio ya tiene un documento de Salida (ya despachados, aunque el
  // inventario físico todavía no refleje el movimiento fuera de la cámara).
  const palletsVisibles = useMemo(() => {
    if (idsCamarasTermino.size === 0 || foliosDespachados.size === 0) return pallets;
    return pallets.filter((p) => {
      const esCamaraTermino = idsCamarasTermino.has(p.idCamara);
      const yaDespachado = foliosDespachados.has(String(p.folioPallet ?? "").trim());
      return !(esCamaraTermino && yaDespachado);
    });
  }, [pallets, idsCamarasTermino, foliosDespachados]);

  // --- DEBUG TEMPORAL: quitar una vez que el filtro quede confirmado funcionando ---
  useEffect(() => {
    if (camaras.length === 0) return;
    console.debug(
      "[DEBUG cámaras] nombres crudos:",
      camaras.map((c) => ({ idCamara: c.idCamara, nombre: JSON.stringify(c.nombre) }))
    );
    console.debug("[DEBUG cámaras] idsCamarasTermino calculados:", Array.from(idsCamarasTermino));
    console.debug(
      "[DEBUG folios] cantidad recibida:",
      foliosDespachados.size,
      "muestra:",
      Array.from(foliosDespachados).slice(0, 8)
    );
    const palletsEnTermino = pallets.filter((p) => idsCamarasTermino.has(p.idCamara));
    console.debug(
      "[DEBUG pallets en cámaras de término]",
      palletsEnTermino.map((p) => ({
        folioPallet: JSON.stringify(p.folioPallet),
        idCamara: p.idCamara,
        matchExacto: foliosDespachados.has(String(p.folioPallet ?? "").trim()),
      }))
    );
  }, [camaras, pallets, idsCamarasTermino, foliosDespachados]);
  // --- FIN DEBUG TEMPORAL ---

  const temperaturaPromedioActual = useMemo(() => {
    const valores = palletsVisibles
      .filter((p) => p.ultimaTemperatura != null && p.ultimaTemperatura !== "")
      .map((p) => Number(p.ultimaTemperatura))
      .filter((n) => !Number.isNaN(n));
    if (valores.length === 0) return null;
    return valores.reduce((a, b) => a + b, 0) / valores.length;
  }, [palletsVisibles]);

  const fetchData = useCallback(async (showToastOnError = true) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const [resCamaras, resPallets, resFolios] = await Promise.all([
        axios.get(EP_CAMARAS),
        axios.get(EP_PALLETS_EN_CAMARA),
        // Si esta consulta falla, no debe tumbar la carga principal de cámaras/pallets:
        // simplemente no se filtra nada ese ciclo.
        axios.get(EP_FOLIOS_DESPACHADOS).catch((e) => {
          console.error(e);
          return { data: [] };
        }),
      ]);
      const camarasData = parseResponse(resCamaras.data);
      const palletsData = parseResponse(resPallets.data);
      const foliosData = parseResponse(resFolios.data);

      if (!Array.isArray(camarasData) || !Array.isArray(palletsData)) {
        throw new Error("Respuesta inesperada del servidor.");
      }

      setCamaras(camarasData);
      setPallets(palletsData);
      setFoliosDespachados(
        new Set(
          (Array.isArray(foliosData) ? foliosData : [])
            .map((f) => String(f.numeroFolioOrigen ?? "").trim())
            .filter(Boolean)
        )
      );
      setLastUpdated(dayjs());
    } catch (e) {
      console.error(e);
      if (showToastOnError) toast.error("No se pudo actualizar el estado de las cámaras.");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling de datos, pausado si la pestaña no está visible o autoRefresh está apagado
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchData(false);
    }, DATA_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Tick liviano solo para refrescar "tiempo transcurrido" en pantalla entre fetchs
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, []);

  const palletsPorCamara = useMemo(() => {
    const map = new Map();
    palletsVisibles.forEach((p) => {
      const key = p.idCamara;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    return map;
  }, [palletsVisibles]);

  const totalAlertas = useMemo(() => palletsVisibles.filter(isPalletEnAlerta).length, [palletsVisibles]);

  // Cámaras con alerta primero, luego el resto en su orden natural
  const camarasOrdenadas = useMemo(() => {
    return [...camaras].sort((a, b) => {
      const alertasA = (palletsPorCamara.get(a.idCamara) || []).filter(isPalletEnAlerta).length;
      const alertasB = (palletsPorCamara.get(b.idCamara) || []).filter(isPalletEnAlerta).length;
      if (alertasA !== alertasB) return alertasB - alertasA;
      return a.idCamara - b.idCamara;
    });
  }, [camaras, palletsPorCamara]);

  /* ---------- Modal tarja ---------- */

  const fetchTarja = useCallback(async (folioPallet, { force = false } = {}) => {
    setTarjaFolio(folioPallet);
    setTarjaModalOpen(true);

    if (!force && tarjaCacheRef.current.has(folioPallet)) {
      setTarjaLineas(tarjaCacheRef.current.get(folioPallet));
      return;
    }

    setTarjaLoading(true);
    setTarjaLineas([]);
    try {
      const res = await axios.get(EP_FOLIO_TARJA, { params: { numeroFolio: folioPallet } });
      const data = parseResponse(res.data);
      const lineas = Array.isArray(data) ? data : [];
      tarjaCacheRef.current.set(folioPallet, lineas);
      setTarjaLineas(lineas);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar la información de la tarja.");
    } finally {
      setTarjaLoading(false);
    }
  }, []);

  const fetchHistorialTemperatura = useCallback(async (folioPallet, { force = false } = {}) => {
    if (!force && historialTempCacheRef.current.has(folioPallet)) {
      setHistorialTemp(historialTempCacheRef.current.get(folioPallet));
      return;
    }
    setHistorialTempLoading(true);
    setHistorialTemp([]);
    try {
      const res = await axios.get(EP_HISTORIAL_TEMPERATURA, { params: { folioPallet } });
      const data = parseResponse(res.data);
      const lista = Array.isArray(data) ? data : [];
      historialTempCacheRef.current.set(folioPallet, lista);
      setHistorialTemp(lista);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el historial de temperaturas.");
    } finally {
      setHistorialTempLoading(false);
    }
  }, []);

  const fetchHistorialMovimientos = useCallback(async (folioPallet, { force = false } = {}) => {
    if (!force && historialMovCacheRef.current.has(folioPallet)) {
      setHistorialMov(historialMovCacheRef.current.get(folioPallet));
      return;
    }
    setHistorialMovLoading(true);
    setHistorialMov([]);
    try {
      const res = await axios.get(EP_HISTORIAL_MOVIMIENTOS, { params: { folioPallet } });
      const data = parseResponse(res.data);
      const lista = Array.isArray(data) ? data : [];
      historialMovCacheRef.current.set(folioPallet, lista);
      setHistorialMov(lista);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el historial de movimientos.");
    } finally {
      setHistorialMovLoading(false);
    }
  }, []);

  const chartDataTemperatura = useMemo(
    () =>
      historialTemp.map((r) => ({
        fecha: dayjs(r.fechaHora).format("DD/MM HH:mm"),
        temperatura: Number(r.temperatura),
      })),
    [historialTemp]
  );

  const [selectedPallet, setSelectedPallet] = useState(null);

  const handleSelectPallet = useCallback(
    (pallet) => {
      setSelectedPallet(pallet);
      setActiveTab("tarja");
      setFotoAmpliada(null);
      fetchTarja(pallet.folioPallet);
      fetchHistorialTemperatura(pallet.folioPallet);
      fetchHistorialMovimientos(pallet.folioPallet);
    },
    [fetchTarja, fetchHistorialTemperatura, fetchHistorialMovimientos]
  );

  const ESTADO_BADGE_ESTILOS = {
    alerta: "bg-red-100 text-red-700",
    normal: "bg-green-100 text-green-700",
    neutral: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="bg-white p-4 rounded-xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Snowflake className="h-5 w-5" style={{ color: ACCENT }} />
          <h2 className="text-lg font-bold text-gray-900 m-0">Estado de Cámaras de Frío</h2>
          {totalAlertas > 0 && (
            <Badge variant="red">
              {totalAlertas} {totalAlertas === 1 ? "pallet" : "pallets"} excedido{totalAlertas === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Actualizado {lastUpdated.format("HH:mm:ss")}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh ? "Pausar actualización automática" : "Reanudar actualización automática"}
          >
            {autoRefresh ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {autoRefresh ? "Auto" : "Pausado"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              fetchData(true);
              fetchMetricasDespacho();
            }}
            loading={loading}
          >
            <RefreshCw className="h-4 w-4" /> Actualizar
          </Button>
        </div>
      </div>

      {/* Métricas globales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <MetricCard
          icon={Thermometer}
          label="Temperatura promedio actual"
          value={temperaturaPromedioActual != null ? `${temperaturaPromedioActual.toFixed(1)}°C` : "—"}
          sublabel={`${palletsVisibles.length} ${palletsVisibles.length === 1 ? "pallet en cámara" : "pallets en cámara"}`}
        />
        <MetricCard
          icon={Truck}
          label="Tiempo promedio hasta despacho"
          value={metricasLoading ? "…" : formatHorasDuracion(tiempoPromedioDespacho)}
          sublabel={palletsConsiderados != null ? `Basado en ${palletsConsiderados} pallets (últimos 90 días)` : undefined}
        />
      </div>

      <Leyenda />

      {/* Grid de cámaras */}
      {camaras.length === 0 && !loading ? (
        <div className="text-gray-400 text-sm py-8 text-center">No hay cámaras registradas.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {camarasOrdenadas.map((c) => (
            <CamaraCard
              key={c.idCamara}
              camara={c}
              pallets={palletsPorCamara.get(c.idCamara) || []}
              onSelectPallet={handleSelectPallet}
            />
          ))}
        </div>
      )}

      {/* Modal detalle de pallet */}
      <Dialog open={tarjaModalOpen} onOpenChange={setTarjaModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-4 w-4" style={{ color: ACCENT }} />
              Pallet {tarjaFolio}
            </DialogTitle>
          </DialogHeader>

          {selectedPallet && (
            <div className="flex flex-wrap items-center gap-2 -mt-1 mb-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  ESTADO_BADGE_ESTILOS[getPalletEstado(selectedPallet)]
                }`}
              >
                {selectedPallet.nombreCamara}
              </span>
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Thermometer className="h-3.5 w-3.5" />
                {selectedPallet.ultimaTemperatura != null
                  ? `${Number(selectedPallet.ultimaTemperatura).toFixed(1)}°C`
                  : "—"}
              </span>
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Clock className="h-3.5 w-3.5" />
                Desde {dayjs(selectedPallet.fechaHora).format("DD/MM HH:mm")}
              </span>
            </div>
          )}

          <div className="flex gap-1 border-b border-gray-100 mb-3">
            {[
              { key: "tarja", label: "Tarja", icon: Package },
              { key: "temperaturas", label: "Temperaturas", icon: Thermometer },
              { key: "movimientos", label: "Movimientos", icon: ArrowLeftRight },
            ].map((t) => {
              const TabIcon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === t.key
                      ? "border-current text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  style={activeTab === t.key ? { color: ACCENT, borderColor: ACCENT } : undefined}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <DialogBody className="space-y-4 max-h-[60vh] overflow-y-auto">
            {activeTab === "tarja" && (
              tarjaLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : tarjaLineas.length === 0 ? (
                <div className="text-gray-400 text-sm py-6 text-center">
                  No se encontró información de tarja para este pallet.
                </div>
              ) : (
                tarjaLineas.map((linea, idx) => (
                  <div key={idx} className={idx > 0 ? "pt-4 border-t border-gray-100" : ""}>
                    {tarjaLineas.length > 1 && (
                      <div className="text-xs font-semibold text-gray-500 mb-2">
                        Línea {linea.lineaFolio ?? idx + 1}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <Field label="Especie" value={trim(linea.especie)} />
                      <Field label="Variedad" value={trim(linea.variedad)} />
                      <Field label="Marca" value={trim(linea.marca)} />
                      <Field label="Embalaje" value={trim(linea.embaleja)} />
                      <Field label="Productor" value={trim(linea.productor)} className="col-span-2" />
                      <Field label="Huerto" value={trim(linea.huerto)} className="col-span-2" />
                      <Field
                        label="Cantidad"
                        value={linea.cantidad != null ? `${linea.cantidad} cajas` : "—"}
                      />
                      <Field
                        label="Kg Netos"
                        value={linea.kgNetos != null ? `${Number(linea.kgNetos).toFixed(2)} kg` : "—"}
                      />
                      <Field label="Categoría" value={trim(linea.categoria)} />
                      <Field label="Calibre" value={trim(linea.calibre)} />
                      <Field label="OP" value={trim(linea.OP)} />
                      <Field
                        label="Fecha Embalaje"
                        value={linea.fechaEmbalaje ? dayjs(linea.fechaEmbalaje).format("DD/MM/YYYY") : "—"}
                      />
                    </div>
                  </div>
                ))
              )
            )}

            {activeTab === "temperaturas" && (
              historialTempLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : historialTemp.length === 0 ? (
                <div className="text-gray-400 text-sm py-6 text-center">
                  No hay lecturas de temperatura registradas para este pallet.
                </div>
              ) : (
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartDataTemperatura} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 11 }} minTickGap={20} />
                        <YAxis tick={{ fontSize: 11 }} width={40} unit="°C" />
                        <RechartsTooltip formatter={(value) => [`${value}°C`, "Temperatura"]} />
                        <Line type="monotone" dataKey="temperatura" stroke={ACCENT} strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 max-h-40 overflow-y-auto border border-gray-100 rounded-lg bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-medium">Fecha y hora</th>
                          <th className="text-right px-3 py-1.5 font-medium">Temperatura</th>
                          <th className="text-center px-3 py-1.5 font-medium">Foto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historialTemp.map((r) => (
                          <tr key={r.idRegistro} className="border-t border-gray-50">
                            <td className="px-3 py-1.5 text-gray-700">
                              {dayjs(r.fechaHora).format("DD/MM/YYYY HH:mm")}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-gray-800">
                              {Number(r.temperatura).toFixed(1)}°C
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              {r.fotoUrl ? (
                                <button
                                  type="button"
                                  onClick={() => setFotoAmpliada(r.fotoUrl)}
                                  className="inline-block rounded-md border border-gray-200 hover:opacity-80 transition-opacity"
                                  title="Ver foto del termómetro"
                                >
                                  <img
                                    src={r.fotoUrl}
                                    alt="Foto del termómetro"
                                    className="h-9 w-9 rounded-md object-cover"
                                    loading="lazy"
                                  />
                                </button>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}

            {activeTab === "movimientos" && (
              historialMovLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : historialMov.length === 0 ? (
                <div className="text-gray-400 text-sm py-6 text-center">
                  No hay movimientos registrados para este pallet.
                </div>
              ) : (
                <div>
                  {historialMov.map((m, idx) => {
                    const esEntrada = String(m.tipoMovimiento).toUpperCase() === "ENTRADA";
                    const esUltimo = idx === historialMov.length - 1;
                    return (
                      <div key={idx} className="relative flex items-start gap-3 pb-4">
                        {!esUltimo && (
                          <div className="absolute left-[11px] top-6 bottom-0 w-px bg-gray-200" />
                        )}
                        <div
                          className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                            esEntrada ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600"
                          }`}
                        >
                          {esEntrada ? (
                            <ArrowDownToLine className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowUpFromLine className="h-3.5 w-3.5" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            {esEntrada ? "Entrada a" : "Salida de"} {trim(m.nombreCamara)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {dayjs(m.fechaHora).format("DD/MM/YYYY HH:mm")}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTarjaModalOpen(false)}>
              Cerrar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                fetchTarja(tarjaFolio, { force: true });
                fetchHistorialTemperatura(tarjaFolio, { force: true });
                fetchHistorialMovimientos(tarjaFolio, { force: true });
              }}
              loading={tarjaLoading || historialTempLoading || historialMovLoading}
            >
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
          </DialogFooter>

          {/* Visor ampliado de la foto del termómetro (subida desde la app).
              Va DENTRO de DialogContent (no como hermano del Dialog) y usa "absolute"
              en vez de "fixed": así el clic queda contenido en el modal y Radix no lo
              interpreta como "clic afuera", que cerraría el Dialog completo en vez de
              solo la foto. */}
          {fotoAmpliada && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-black/80 p-4"
              onClick={() => setFotoAmpliada(null)}
            >
              <button
                type="button"
                onClick={() => setFotoAmpliada(null)}
                className="absolute top-3 right-3 text-white/80 hover:text-white"
                title="Cerrar"
              >
                <X className="h-6 w-6" />
              </button>
              <img
                src={fotoAmpliada}
                alt="Foto del termómetro ampliada"
                className="max-h-[85%] max-w-[85%] rounded-lg object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisorCamarasFrio;